/**
 * @module OperationProgress
 * @purpose Reusable platform facility (FR16/FR16a): warn-before-start copy,
 *   a dynamic progress bar, and a terminal completion dialogue for ANY
 *   long-running background operation exposed via the shared
 *   `operation_progress` read path. Takes no feature-specific strings --
 *   every label comes from i18n or the caller's `warningCopy` slot.
 * @depends useOperationProgress, operation-progress.parts
 * @dependedOnBy container-triage (inventory trigger), upload-receipt-modal
 *   (staged-verify registration)
 *
 * @rules
 * - State: delegated entirely to useOperationProgress; this file is pure composition
 * - Errors: loading/error/unknown-operation/running/terminal are all handled --
 *   a spinner never persists indefinitely (poll-failure and 404 both resolve)
 * - Accessibility: ARIA progressbar semantics; completion dialogue is a
 *   keyboard-reachable role="dialog"
 */

import { type ReactElement, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useOperationProgress } from '../../../hooks/use-operation-progress'
import type { OperationProgress as OperationProgressData } from '../../../types/evidence-containers'
import {
  CompletionDialogue,
  DeterminateBar,
  ErrorState,
  IndeterminateBar,
  LoadingState,
  UnknownOperationState,
} from './operation-progress.parts'

export interface OperationProgressProps {
  caseId: string
  operationId: string
  warningCopy?: ReactElement | string
  onComplete?: (progress: OperationProgressData) => void
}

function useCompletionCallback(
  progress: OperationProgressData | null,
  onComplete: ((progress: OperationProgressData) => void) | undefined
): void {
  const notifiedRef = useRef(false)
  useEffect(() => {
    if (!progress || notifiedRef.current) return
    if (progress.status !== 'succeeded' && progress.status !== 'failed') return
    notifiedRef.current = true
    onComplete?.(progress)
  }, [progress, onComplete])
}

type TranslateFn = ReturnType<typeof useTranslation<'cases'>>['t']

function ProgressBody({
  progress,
  onAcknowledge,
  t,
}: {
  progress: OperationProgressData
  onAcknowledge: () => void
  t: TranslateFn
}): ReactElement {
  if (progress.status === 'succeeded' || progress.status === 'failed') {
    return (
      <CompletionDialogue
        succeeded={progress.status === 'succeeded'}
        outcomeSummary={progress.outcomeSummary}
        onAcknowledge={onAcknowledge}
        t={t}
      />
    )
  }
  if (progress.unitsTotal !== null) {
    return (
      <DeterminateBar
        unitsDone={progress.unitsDone}
        unitsTotal={progress.unitsTotal}
        stepLabel={progress.stepLabel}
        t={t}
      />
    )
  }
  return <IndeterminateBar unitsDone={progress.unitsDone} stepLabel={progress.stepLabel} t={t} />
}

interface StatusAreaProps {
  unknownOperation: boolean
  error: string | null
  loading: boolean
  progress: OperationProgressData | null
  retry: () => void
  onAcknowledge: () => void
  t: TranslateFn
}

/**
 * @behaviour Picks exactly one status view: unknown-operation, error+retry,
 *   initial loading, or the live/terminal progress body. Isolated from
 *   OperationProgress so the parent's own branching stays trivial.
 */
function StatusArea({
  unknownOperation,
  error,
  loading,
  progress,
  retry,
  onAcknowledge,
  t,
}: StatusAreaProps): ReactElement {
  if (unknownOperation) return <UnknownOperationState t={t} />
  if (error !== null) return <ErrorState message={error} onRetry={retry} t={t} />
  if (loading && progress === null) return <LoadingState t={t} />
  if (progress !== null) {
    return <ProgressBody progress={progress} onAcknowledge={onAcknowledge} t={t} />
  }
  return <></>
}

/**
 * @behaviour Renders warn-before-start copy while running, a dynamic bar
 *   (percent when `unitsTotal` is known, else indeterminate + step/counter),
 *   and on a terminal status a completion dialogue with an outcome summary.
 * @errors Renders a retry banner after repeated poll failures and a terminal
 *   "unknown operation" banner on 404 -- never leaves a spinner spinning.
 */
export function OperationProgress({
  caseId,
  operationId,
  warningCopy,
  onComplete,
}: OperationProgressProps): ReactElement {
  const { t } = useTranslation('cases')
  const { progress, loading, error, unknownOperation, retry } = useOperationProgress(
    caseId,
    operationId
  )
  useCompletionCallback(progress, onComplete)

  const isRunning = progress !== null && progress.status === 'running'
  const showWarning = warningCopy !== undefined && (loading || isRunning)

  return (
    <div role="region" aria-label={t('operationProgress.regionLabel')} className="space-y-2">
      {showWarning && (
        <p className="text-xs" style={{ color: 'var(--text-dim)' }}>
          {warningCopy}
        </p>
      )}
      <StatusArea
        unknownOperation={unknownOperation}
        error={error}
        loading={loading}
        progress={progress}
        retry={retry}
        onAcknowledge={() => {
          if (progress) onComplete?.(progress)
        }}
        t={t}
      />
    </div>
  )
}
